import type { Component, WebserviceCharacteristic } from '../schema';
import type { EvaluatedValue } from './types';

/**
 * Fetch every `webservice` Characteristic's configured URL via the
 * cloud-side CORS proxy (`{cloudBase}/data/callApi.php`), extract the
 * result by the admin-authored path (XPath for XML, JSONPath subset for
 * JSON), and return a Map of resolved values keyed by Characteristic id.
 *
 * Legacy contract: a single POST per call, body `{ url: <webserviceUrl> }`,
 * raw response (XML string or JSON string) parsed client-side. We keep
 * the same protocol so cloud-side `callApi.php` stays untouched.
 *
 * Errors are isolated per Characteristic: a failed fetch / parse logs a
 * `console.warn` and the id stays absent from the returned Map (which
 * the evaluator treats the same as "no value" — usually null). One bad
 * webservice doesn't poison the whole evaluation pass.
 */
export async function fetchWebserviceValues(
    component: Component,
    cloudBaseUrl: string | undefined,
): Promise<Map<number, EvaluatedValue>> {
    const out = new Map<number, EvaluatedValue>();
    const targets = collectWebserviceChars(component);
    if (targets.length === 0) return out;

    const proxyUrl = buildProxyUrl(cloudBaseUrl);

    // Fire all calls in parallel; one slow webservice can't block the
    // others. Errors are isolated per call.
    await Promise.all(targets.map(async (char) => {
        if (!char.webserviceUrl) return;
        try {
            const body = new URLSearchParams({ url: char.webserviceUrl });
            const res = await fetch(proxyUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body,
            });
            if (!res.ok) {
                console.warn(
                    `[Configento] webservice fetch failed for ${char.altTitle || char.id}: `
                    + `HTTP ${res.status}`,
                );
                return;
            }
            const text = await res.text();
            const value = extractByPath(
                text,
                char.webserviceReturnType ?? 'json',
                char.webservicePath ?? '',
            );
            if (value !== null) out.set(char.id, value);
        } catch (err) {
            console.warn(
                `[Configento] webservice fetch error for ${char.altTitle || char.id}:`,
                err,
            );
        }
    }));

    return out;
}

function collectWebserviceChars(component: Component): WebserviceCharacteristic[] {
    const out: WebserviceCharacteristic[] = [];
    for (const group of component.groups) {
        for (const char of group.characteristics) {
            if (char.type === 'webservice') out.push(char);
        }
    }
    return out;
}

function buildProxyUrl(cloudBaseUrl: string | undefined): string {
    const base = (cloudBaseUrl ?? 'cloud.configento.app').replace(/\/+$/, '');
    const protocol = base.startsWith('http') ? '' : 'https://';
    return `${protocol}${base}/data/callApi.php`;
}

/**
 * Extract a value from the raw response. `returnType` is either `'json'`
 * (apply a JSONPath-style path) or `'xml'` (XPath via `DOMParser`).
 * Anything else falls through to returning the raw text — covers the
 * "merchant wants the whole body" case the legacy storefront also
 * supported by leaving the path empty.
 */
function extractByPath(text: string, returnType: string, path: string): EvaluatedValue {
    const t = returnType.toLowerCase();
    if (t === 'xml') return extractXml(text, path);
    if (t === 'json') return extractJson(text, path);
    return text.trim() === '' ? null : text;
}

function extractXml(text: string, path: string): EvaluatedValue {
    if (typeof DOMParser === 'undefined') return null;
    try {
        const doc = new DOMParser().parseFromString(text, 'text/xml');
        if (!path) return doc.documentElement?.textContent ?? null;
        const result = doc.evaluate(path, doc, null, XPathResult.STRING_TYPE, null);
        const s = result.stringValue;
        return s === '' ? null : s;
    } catch (err) {
        console.warn('[Configento] XPath extraction failed:', err);
        return null;
    }
}

function extractJson(text: string, path: string): EvaluatedValue {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch (err) {
        console.warn('[Configento] JSON parse failed:', err);
        return null;
    }
    if (!path) return toEvalValue(parsed);
    return toEvalValue(pickJsonPath(parsed, path));
}

/**
 * Tiny JSONPath subset — enough to cover the legacy `jsonPath()` calls
 * Configento templates actually use. Supports:
 *
 *   `$.foo.bar`     dot-walk
 *   `foo.bar`       leading `$.` optional
 *   `foo[0].bar`    array index
 *   `foo["bar"]`    bracket notation with quotes
 *
 * Anything more exotic (filter expressions, descendant operators) is
 * unsupported — the result will just be undefined and the evaluator
 * sees it as no-value.
 */
function pickJsonPath(root: unknown, rawPath: string): unknown {
    const path = rawPath.replace(/^\$\.?/, '');
    if (path === '') return root;
    const tokens = tokenisePath(path);
    let cur: unknown = root;
    for (const tok of tokens) {
        if (cur === null || typeof cur !== 'object') return undefined;
        cur = (cur as Record<string, unknown>)[tok];
        if (cur === undefined) return undefined;
    }
    return cur;
}

function tokenisePath(path: string): string[] {
    // Match either `.foo`, `["foo"]`, `['foo']`, or `[0]`. We strip the
    // brackets/quotes and emit each segment as a plain string key.
    const out: string[] = [];
    const re = /(?:^|\.)([A-Za-z_$][\w$]*)|\[\s*"([^"]+)"\s*\]|\[\s*'([^']+)'\s*\]|\[\s*(\d+)\s*\]/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(path)) !== null) {
        const key = m[1] ?? m[2] ?? m[3] ?? m[4];
        if (key !== undefined) out.push(key);
    }
    return out;
}

function toEvalValue(v: unknown): EvaluatedValue {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') return v;
    if (typeof v === 'boolean') return v ? 1 : 0;
    // Arrays / objects can't flow into expression arithmetic — surface
    // null so the caller's fallback kicks in.
    return null;
}
